// Moteur de calcul des documents (devis, commandes) — miroir exact de la
// fonction SQL public.compute_totals. Le test src/lib/__tests__/totals.test.ts
// et le parcours e2e « moteur » vérifient que les deux donnent le même résultat.
//
// Ordre : ligne → sous-total → remise globale → frais → base → taxe → total →
// acompte → solde. Arrondi half-up à 2 décimales à chaque étape.

export type DiscountType = "percent" | "amount" | null;

export interface LineInput { quantity: number; unit_price: number; discount_type?: DiscountType; discount_value?: number | null }
export interface DocInput {
  items: LineInput[];
  fees: { amount: number }[];
  global_discount_type?: DiscountType;
  global_discount_value?: number | null;
  tax_rate_pct?: number | null;
  deposit_pct?: number | null;
}
export interface Totals {
  subtotal: number; global_discount_amount: number; fees_total: number; taxable_base: number;
  tax_amount: number; total: number; deposit_amount: number; balance_amount: number;
}

/** Arrondi half-up à 2 décimales, comme round(numeric, 2) de PostgreSQL. */
export function round2(n: number): number {
  const s = Math.sign(n) || 1;
  return (s * Math.round(Math.abs(n) * 100 + 1e-9)) / 100;
}

export function lineTotal(l: LineInput): number {
  const gross = round2(l.quantity * l.unit_price);
  const v = l.discount_value ?? 0;
  const net = l.discount_type === "percent" ? gross * (1 - v / 100) : l.discount_type === "amount" ? gross - v : gross;
  return Math.max(0, round2(net));
}

export function computeTotals(doc: DocInput): Totals {
  const subtotal = round2(doc.items.reduce((s, l) => s + lineTotal(l), 0));
  const gv = doc.global_discount_value ?? 0;
  const global_discount_amount =
    doc.global_discount_type === "percent" ? round2((subtotal * gv) / 100)
    : doc.global_discount_type === "amount" ? Math.min(subtotal, round2(gv)) : 0;
  const fees_total = round2(doc.fees.reduce((s, f) => s + round2(f.amount), 0));
  const taxable_base = round2(subtotal - global_discount_amount + fees_total);
  const tax_amount = round2((taxable_base * (doc.tax_rate_pct ?? 0)) / 100);
  const total = round2(taxable_base + tax_amount);
  const deposit_amount = round2((total * (doc.deposit_pct ?? 0)) / 100);
  return { subtotal, global_discount_amount, fees_total, taxable_base, tax_amount, total, deposit_amount, balance_amount: round2(total - deposit_amount) };
}
